import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'reactstrap';
import { ModalWindow } from "src/app/shared/components/modal-window/ModalWindow";
import i18n from "src/app/shared/localization/i18n";
import { TelegramAuthStatus } from "../enums/TelegramAuthStatus";
import { _getQrStatusMessage } from "src/app/shared/helpers/telegram-auth-helper/TelegramAuthHelper";
import { QR_POLLING_INTERVAL_SEC, QR_TIMER_UPDATE_INTERVAL_MS, TERMINAL_STATUSES } from "./ChannelAuthQrModalConstants";
import { IChannelQrModalProps } from "./~types/ChannelAuthQrModalProps";

export const ChannelAuthQrModal: React.FC<IChannelQrModalProps> = ({
    isOpen,
    onClose,
    channelId,
    qrAuthData,
    qrAuthIsLoading,
    fetchQrCode
}) => {
    const [timer, setTimer] = useState<number>(QR_POLLING_INTERVAL_SEC);

    const pollingIntervalId = useRef<ReturnType<typeof setInterval> | null>(null);
    const qrAuthDataRef = useRef(qrAuthData);
    const fetchQrCodeRef = useRef(fetchQrCode);

    useEffect(() => {
        qrAuthDataRef.current = qrAuthData;
    }, [qrAuthData]);

    useEffect(() => {
        fetchQrCodeRef.current = fetchQrCode;
    }, [fetchQrCode]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalId.current) {
            clearInterval(pollingIntervalId.current);
            pollingIntervalId.current = null;
        }
    }, []);

    const startQrPolling = useCallback((id: number) => {
        stopPolling();

        fetchQrCodeRef.current(id);
        setTimer(QR_POLLING_INTERVAL_SEC);

        pollingIntervalId.current = setInterval(() => {
            const status = qrAuthDataRef.current?.status;

            if (status && TERMINAL_STATUSES.includes(status as TelegramAuthStatus)) {
                stopPolling();
                return;
            }

            setTimer((prevTimer) => {
                if (prevTimer <= 1) {
                    fetchQrCodeRef.current(id);
                    return QR_POLLING_INTERVAL_SEC;
                }
                return prevTimer - 1;
            });
        }, QR_TIMER_UPDATE_INTERVAL_MS);
    }, [stopPolling]);

    useEffect(() => {
        if (isOpen && channelId) {
            startQrPolling(channelId);
        } else if (!isOpen) {
            stopPolling();
        }
        return () => stopPolling();
    }, [isOpen, channelId, startQrPolling, stopPolling]);

    const onManualRefreshHandler = () => {
        if (channelId) {
            startQrPolling(channelId);
        }
    };

    const onCloseQrModalHandler = () => {
        onClose();
        stopPolling();
    };

    const renderQrModalBody = (): React.ReactNode => {

        if (qrAuthIsLoading && !qrAuthData) {
            return <div className="text-center">{i18n.t('qr.telegram.loading')}</div>;
        }

        const status = qrAuthData?.status;

        const refreshBtn = status === TelegramAuthStatus.WaitingForScan ||
            status === TelegramAuthStatus.Initializing ||
            status === TelegramAuthStatus.Authorized ? (
            <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <Button
                    size="sm"
                    color="primary"
                    outline
                    onClick={onManualRefreshHandler}
                    disabled={qrAuthIsLoading}
                >
                    <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '5px' }}>refresh</i>
                    {i18n.t('qr.telegram.refresh')} {timer > 0 ? `(${timer}s)` : ''}
                </Button>
            </div>
        ) : null;

        if (qrAuthData) {
            if (status === TelegramAuthStatus.WaitingForScan && qrAuthData.qrUrl) {
                return (
                    <div style={{ textAlign: 'center' }}>
                        <p>{i18n.t('qr.telegram.scanCode')}</p>
                        <img src={qrAuthData.qrUrl} alt="QR" style={{ maxWidth: '100%' }} />
                        {refreshBtn}
                    </div>
                );
            } else if (status === TelegramAuthStatus.Authorized) {
                return (
                    <div>
                        <div style={{ textAlign: 'center' }}>{i18n.t('qr.telegram.authorized')}</div>
                        {refreshBtn}
                    </div>
                );
            } else {
                const statusInfo = status ? _getQrStatusMessage(status) : { text: '' };
                return (
                    <div style={{ textAlign: 'center' }}>
                        <p>{statusInfo.text}</p>
                        {refreshBtn}
                    </div>
                );
            }
        }
        return null;
    };

    return (
        <ModalWindow
            isOpen={isOpen}
            header={<div>{i18n.t('qr.telegram.enterByQr')}</div>}
            body={renderQrModalBody() as React.ReactElement}
            footer={<Button onClick={onCloseQrModalHandler}>{i18n.t("common.close")}</Button>}
            onClose={onCloseQrModalHandler}
        />
    );
};